import http from 'api/httpClient';

export type BillSortKey =
  | 'reference_no'
  | 'branch_name'
  | 'bill_date'
  | 'due_date'
  | 'overdue_days'
  | 'outstanding';

export type AraYearMonths = {
  year: string;
  months: number[];
};

export type FetchAraYearsResponse = {
  years: AraYearMonths[];
};

export interface Subordinate {
  ecode: string;
  full_name: string;
}

export interface SubordinatesResponse {
  items: Subordinate[];
}

export interface Division {
  ou_code: string;
  ou_name: string;
}

export interface DivisionsResponse {
  items: Division[];
}

export type PaymentType = 'to_pay' | 'to_be_billed';
export type PaymentTypeValue = PaymentType | '';

export interface AraTableRow {
  Customer: string;
  CreditPeriod: number;
  TotalOutstanding: number;
  NotDue: number;
  TotalOverdue: number;
  Bucket1_30: number;
  Bucket31_60: number;
  Bucket61_90: number;
  Bucket91_120: number;
  Bucket121_180: number;
  BucketOver180: number;
  Legal: number;
  UnappliedReceipts: number;
  NetOutstanding: number;
}

export interface FetchAraRequest extends Record<string, unknown> {
  year: string;
  month: number;
  ecode?: string;
  division?: string;
  payment_type?: PaymentTypeValue;
  page: number;
  per_page: number;
  sort_by: string;
  sort_order: 'asc' | 'desc';
  search: string | undefined;
}

export interface FetchAraResponse {
  rows: AraTableRow[];
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_previous: boolean;
  has_next: boolean;
  selected_user: string;
  subordinates: string[];
}

export interface BillDetailRow {
  reference_no: string;
  branch_name: string | null;
  bill_date: string | null;
  due_date: string | null;
  overdue_days: number | null;
  outstanding: number;
  collection_responsibility: string | null;
}

export interface BillDetailsResponse {
  customer_name: string;
  customer_code: string;
  items: BillDetailRow[];
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_previous: boolean;
  has_next: boolean;
}


export async function fetchAraYears(): Promise<FetchAraYearsResponse> {
  return http.get('/api/v1/outstanding/years');
}

export async function fetchDivisions(): Promise<DivisionsResponse> {
  return http.get('/api/v1/outstanding/divisions');
}

export async function fetchSubordinates(divisionOuCode?: string): Promise<SubordinatesResponse> {
  const params = new URLSearchParams();
  if (divisionOuCode) {
    params.set('division_ou_code', divisionOuCode);
  }
  const url = `/api/v1/outstanding/subordinates${params.toString() ? `?${params.toString()}` : ''}`;
  return http.get(url);
}

export async function fetchAraData(
  payload: FetchAraRequest
): Promise<FetchAraResponse> {
  const data = await http.post<FetchAraResponse>(
    '/api/v1/outstanding/ageing',
    payload as Record<string, unknown>
  );

  const normalizedRows = (data.rows || []).map((r: any) => ({
    ...r,
    Bucket1_30: r['1-30 days'] ?? 0,
    Bucket31_60: r['31-60 days'] ?? 0,
    Bucket61_90: r['61-90 days'] ?? 0,
    Bucket91_120: r['91-120 days'] ?? 0,
    Bucket121_180: r['121-180 days'] ?? 0,
    BucketOver180: r['> 180 days'] ?? 0,
  }));

  return {
    ...data,
    rows: normalizedRows,
  };
}


export async function fetchBillDetails(payload: {
  customer_name: string;
  year: string;
  month: number;
  ecode: string | undefined;
  division: string | undefined;
  page: number;
  per_page: number;
  sort_by: string;
  sort_order: 'asc' | 'desc';
}): Promise<BillDetailsResponse> {
  return http.post<BillDetailsResponse>('/api/v1/outstanding/get-bill-details', payload);
}
